import { css, styled } from 'canoe';
import React, { useState } from 'react';

import { FormInputValidation } from '@candu-ai/forms/document-types';
import { useManageFormFieldValidation } from '@candu-ai/forms/editor-utils';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { hiddenStyles } from 'commons/utils';
import Node from 'craft/components/Node/Node';
import { BuiltInComponents } from 'craft/constants';
import { useEditorNode, useNodeContext } from 'craft/hooks';
import { AtomicText } from 'craft/resolvers/AtomicText';
import { FormFieldValidationMessage } from 'craft/resolvers/Forms/FormFieldValidationMessage';
import { useFormAncestor, useFormIdFromAncestor } from 'craft/state';
import { CssSelectorId, ToolboxComponentSections } from 'craft/types';

import { CheckIcon } from './CheckIcon';
import { FormLabel } from './FormLabel';
import { getFormFieldNodeRules } from './helpers';
import { CheckboxListToolbox } from './toolbox/CheckboxListToolbox';

const CheckboxList = styled.div<{ columns: number }>`
  ${({ columns }) => css`
    display: grid;
    grid-template-columns: repeat(${columns}, 1fr);
  `}
`;

const CheckboxWrapper = styled.div``;

interface Props {
  formInputId: string;
  formId?: string;
  label: string;
  columns?: number;
  showLabel?: boolean;

  options: { label: string; value?: string }[];
  validation?: FormInputValidation[];
}

export const FormCheckboxList = ({
  formInputId,
  formId,
  label,
  columns = 1,
  showLabel,
  options,

  validation,
}: Props) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const [originalCheckboxValue, setOriginalCheckboxValue] = useState('');
  const { setProps } = useEditorNode(id);

  useFormIdFromAncestor(formId);

  const {
    validationIndexMapping,
    isRequired,
    onSetAtomicValidationProps,
  } = useManageFormFieldValidation({
    setProps,
    validation,
  });

  const checkboxClassName = toCssSelector('form', {
    output: 'html',
    className: 'checkbox',
  });

  const checkboxDivClassName = toCssSelector('form', {
    output: 'html',
    className: 'checkboxDiv',
  });

  const classNames = `${checkboxClassName} ${shouldDisplayErrorState ? ' error' : ''}`.trim();

  const onSetAtomicProps = (value: string, index: number) => {
    const newOptions = options.map((option, idx) => {
      if (idx === index) {
        return {
          ...option,
          label: value,
        };
      }
      return option;
    });
    setProps({ options: newOptions });
  };

  return (
    <Node data-type="form-checkbox">
      {showLabel && <FormLabel id={id} required={isRequired} label={label} />}
      <CheckboxList columns={columns}>
        {options.map((option, index) => (
          <CheckboxWrapper key={`${option.label}-${index.toString()}`}>
            <label
              htmlFor={`${formInputId}-${index}`}
              data-atomictext-node={`${id}-${index}`}
              className={classNames}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                id={`${formInputId}-${index}`}
                name={`${formInputId}-${index}`}
                type="checkbox"
                disabled
                checked={index === 0}
                value={option.value || option.label}
                style={hiddenStyles as any}
              />
              <div className={checkboxDivClassName}>
                <CheckIcon />
              </div>
              <AtomicText
                atomicKey={`${id}-${index}`}
                propKey="options"
                childrenString={option.label}
                onSetProps={(l) => onSetAtomicProps(l, index)}
                onClick={() => setOriginalCheckboxValue(option.label)}
                fallbackToOriginalValue={originalCheckboxValue}
              />
            </label>
          </CheckboxWrapper>
        ))}
      </CheckboxList>
      {isRequired && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="required"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'required')}
          message={validation?.[validationIndexMapping.required]?.message}
        />
      )}
    </Node>
  );
};

FormCheckboxList.craft = {
  displayName: 'Form: Checkboxes',
  name: BuiltInComponents.FormCheckboxList,
  isCanvas: false,
  rules: getFormFieldNodeRules(),
};

FormCheckboxList.toolbox = {
  component: [CheckboxListToolbox],
  schema: {
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.FormCheckboxListDiv,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.FormCheckboxListDiv,
    },
  },
};

FormCheckboxList.selectedToolbar = {};
